// Aranabilir ÇOK seçimli seçici (SearchSelect'in çoklu kipi — tek seçimli bileşen birebir korunur;
// görünüm `search-select-styles` ve süzme `filterSearchOptions` ortak). Sektör paketleri DR17-4:
// listbox `aria-multiselectable`, seçenekte `aria-selected`; Enter/Space ve tıklama seçimi değiştirir
// (liste açık kalır); Esc/Tab/dışarı tıklama kapatır. Seçililer tetikleyicinin altında görünür
// çipler (her çipte kaldırma düğmesi). Görünür etiket çağıranda (`<label htmlFor={id}>`).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { type SearchSelectOption } from "../../lib/search-select";
import { listKeyAction, moreText, searchSelectCore } from "./search-select-core";
import {
  codeStyle,
  hintStyle,
  itemStyle,
  listStyle,
  popStyle,
  rowTextStyle,
  searchStyle,
  triggerStyle,
} from "./search-select-styles";

export interface SearchMultiSelectProps {
  id?: string | undefined;
  ariaLabel?: string | undefined;
  values: readonly string[];
  options: readonly SearchSelectOption[];
  onChangeMany: (values: string[]) => void;
  /** hiçbiri seçili değilken tetikleyici metni */
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  disabled?: boolean | undefined;
  /** seçili sayısı → tetikleyici metni ("2 paket seçili") */
  summary?: ((n: number) => string) | undefined;
  /** çip kaldırma düğmesinin erişilebilir adı */
  removeLabel?: ((label: string) => string) | undefined;
  /** listelenecek en fazla sonuç (varsayılan 200); aşılırsa sonda "+N daha" satırı */
  limit?: number | undefined;
  /** açık başlar (SSR testi / pencere içinde hemen seçim) */
  initialOpen?: boolean | undefined;
}

/** Seçimi değiştirir (saf; sıra seçeneklerin sırasıdır). */
export function toggleMultiValue(
  values: readonly string[],
  value: string,
  options: readonly SearchSelectOption[],
): string[] {
  const set = new Set(values);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return options.map((o) => o.value).filter((v) => set.has(v));
}

export function SearchMultiSelect(props: SearchMultiSelectProps): JSX.Element {
  const {
    id,
    ariaLabel,
    values,
    options,
    onChangeMany,
    placeholder = "Seçin…",
    searchPlaceholder = "Ara…",
    disabled,
    summary = (n: number) => `${n} seçili`,
    removeLabel = (l: string) => `${l} seçimini kaldır`,
  } = props;
  const genId = useId();
  const listId = `${genId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(props.initialOpen ?? false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const selected = useMemo(() => new Set(values), [values]);
  const core = useMemo(
    () =>
      searchSelectCore({
        items: options,
        query,
        keyOf: (o) => o.value,
        labelOf: (o) => o.label,
        codeOf: (o) => o.code,
        hintOf: (o) => o.hint,
        max: props.limit ?? 200,
      }),
    [query, options, props.limit],
  );
  const rows = core.visible;
  const chosen = useMemo(() => options.filter((o) => selected.has(o.value)), [options, selected]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    function onDoc(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [active, open, listId]);

  function toggle(o: SearchSelectOption): void {
    if (o.disabled) return;
    onChangeMany(toggleMultiValue(values, o.value, options));
  }

  const activeId = open && rows[active] ? `${listId}-${active}` : undefined;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0 }}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        style={{ ...triggerStyle, minHeight: 44, fontSize: "0.875rem" }}
      >
        <span style={{ ...rowTextStyle, color: chosen.length ? undefined : "var(--color-text-3)" }}>
          {chosen.length ? summary(chosen.length) : placeholder}
        </span>
        <span aria-hidden="true" style={{ color: "var(--color-text-3)", marginLeft: "auto" }}>
          ▾
        </span>
      </button>
      {chosen.length > 0 ? (
        <ul
          aria-label={ariaLabel}
          className="flex flex-wrap gap-1"
          style={{ listStyle: "none", padding: 0, margin: "6px 0 0" }}
        >
          {chosen.map((o) => (
            <li key={o.value} style={chipStyle}>
              <span>{o.label}</span>
              <button
                type="button"
                aria-label={removeLabel(o.label)}
                disabled={disabled}
                onClick={() => toggle(o)}
                style={chipRemoveStyle}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open ? (
        <div style={popStyle}>
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            {...(activeId ? { "aria-activedescendant": activeId } : {})}
            aria-label={`${ariaLabel ?? "Seçenek"} ara`}
            placeholder={searchPlaceholder}
            value={query}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              const act = listKeyAction(
                e.key === " " && query === "" ? "Enter" : e.key,
                active,
                rows.length,
              );
              if (typeof act === "number") {
                e.preventDefault();
                setActive(act);
              } else if (act === "select") {
                e.preventDefault();
                if (rows[active]) toggle(rows[active]);
              } else if (act === "close") {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }
                setOpen(false);
              }
            }}
            style={searchStyle}
          />
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-label={ariaLabel}
            style={listStyle}
          >
            {rows.length === 0 ? (
              <li style={{ ...itemStyle, color: "var(--color-text-3)", cursor: "default" }}>
                Eşleşen seçenek yok
              </li>
            ) : null}
            {rows.map((o, i) => {
              const on = selected.has(o.value);
              return (
                <li
                  key={o.value}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={on}
                  aria-disabled={o.disabled ? true : undefined}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggle(o)}
                  style={{
                    ...itemStyle,
                    opacity: o.disabled ? 0.5 : 1,
                    background:
                      i === active
                        ? "color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))"
                        : "transparent",
                    fontWeight: on ? 700 : 400,
                  }}
                >
                  <span aria-hidden="true" style={{ width: 18, flexShrink: 0 }}>
                    {on ? "✓" : ""}
                  </span>
                  <span style={rowTextStyle}>
                    <span>{o.label}</span>
                    {o.code ? <span style={codeStyle}>{o.code}</span> : null}
                    {o.hint ? <span style={hintStyle}>{o.hint}</span> : null}
                  </span>
                </li>
              );
            })}
            {core.more > 0 ? (
              <li
                role="presentation"
                style={{ ...itemStyle, color: "var(--color-text-3)", cursor: "default" }}
              >
                {moreText(core.more)}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  borderRadius: 999,
  padding: "0 0 0 10px",
  fontSize: "0.8rem",
  fontWeight: 600,
  background: "color-mix(in srgb, var(--color-primary) 10%, white)",
  color: "var(--color-primary-text)",
} as const;
const chipRemoveStyle = {
  minWidth: 44,
  minHeight: 44,
  margin: "-6px 0",
  border: 0,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  fontSize: "1rem",
  lineHeight: 1,
} as const;
